import type { CreditQualityBucket } from '../api/chartDataTypes';

export type CreditQualitySummary = {
    /** Plain-text version — used by the NVDA live region. */
    text: string;
    /** Same content with the percentage wrapped in <strong> for the visual
     *  banner. The grade and display tokens are HTML-escaped here because the
     *  backend may emit values like "< 1" — a bare `<` would be parsed as the
     *  start of an HTML tag when the shell splices this string via
     *  dangerouslySetInnerHTML. */
    html: string;
};

/** Minimal HTML escape — only the characters the backend can plausibly emit
 *  (so far just `<` and `>` inside display strings like "< 1"). Kept inline
 *  rather than importing a library because the surface area is tiny. */
function escapeHtml(value: string): string {
    return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/**
 * "BBB: 94.08 percent."   or   "Not Rated: < 1 percent."
 *
 * Spells out `percent` in the text variant because NVDA's default punctuation
 * level does not reliably read `%`. The HTML variant uses `%` for visual
 * brevity and escapes the grade + display tokens so values containing `<`
 * (e.g. "< 1") render literally instead of being parsed as a tag.
 */
export function buildCreditQualitySummarySentence(bucket: CreditQualityBucket): CreditQualitySummary {
    return {
        text: `${bucket.grade}: ${bucket.display} percent.`,
        html: `${escapeHtml(bucket.grade)}: <strong>${escapeHtml(bucket.display)}%</strong>.`,
    };
}
